import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, TrendingUp } from "lucide-react";
import CtaButton from "./CtaButton";
import { SCREENSHOTS } from "../../data/content";

const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
      {/* background texture */}
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 75%)",
          }}
        />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[400px] w-[700px] bg-cyan-500/10 blur-[140px] rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 border border-white/10 bg-white/[0.02] font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400"
          data-testid="hero-badge"
        >
          <span className="h-1.5 w-1.5 bg-green-400 rounded-full animate-pulse" />
          Tutorial & Onboarding · Covered Call Rolling Terminal
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-white max-w-4xl leading-[1.02]"
          data-testid="hero-heading"
        >
          Roll your covered calls
          <br />
          <span className="text-zinc-500">without the spreadsheet chaos.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.12 }}
          className="mt-7 max-w-2xl text-lg text-zinc-400 leading-relaxed"
          data-testid="hero-subheading"
        >
          ROLL.OPS is a focused, educational terminal for income traders. You
          enter your own positions and model rolls by hand using real, live
          market data from Tradier — no broker connection. This page shows
          exactly how it works, then sends you into the live app.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <CtaButton ctaId="hero-primary-cta" label="Open ROLL.OPS" location="hero" className="px-7 py-3.5">
            Open ROLL.OPS
          </CtaButton>
          <a
            href="#tutorial"
            data-testid="hero-secondary-cta"
            className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-medium text-white border border-white/20 hover:bg-white/5 hover:border-white/40 transition-all duration-300"
          >
            View tutorial
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
        </motion.div>

        {/* Product shot */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16 md:mt-20"
          data-testid="hero-product-shot"
        >
          <div className="relative border border-white/10 bg-[#0A0A0A]">
            {/* browser chrome */}
            <div className="flex items-center gap-2 px-4 h-10 border-b border-white/10 bg-black/40">
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <div className="ml-4 flex items-center gap-2 px-3 h-6 bg-white/[0.03] border border-white/10 font-mono text-[11px] text-zinc-500">
                <TrendingUp className="h-3 w-3 text-cyan-400" />
                call-strategy-hub-1 · live terminal
              </div>
            </div>
            <img
              src={SCREENSHOTS.analysis}
              alt="ROLL.OPS covered call roll-analysis terminal"
              className="w-full block"
              loading="eager"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
